import { useRef, useState, useEffect, ReactNode } from 'react';
import { Skeleton } from '@/components/ui/skeleton';

interface LazySectionProps {
  children: ReactNode;
  /** Minimum height for the placeholder */
  minHeight?: string;
  /** Root margin for early loading (e.g., "200px" to load 200px before visible) */
  rootMargin?: string;
  /** Show skeleton placeholder while loading */
  showSkeleton?: boolean;
}

/**
 * LazySection - Defers rendering of below-fold sections until they're near the viewport.
 * This reduces initial JavaScript execution and API calls for content that isn't immediately visible.
 */
const LazySection = ({ 
  children, 
  minHeight = '200px', 
  rootMargin = '100px',
  showSkeleton = true 
}: LazySectionProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // If IntersectionObserver isn't supported, render immediately
    if (!('IntersectionObserver' in window)) {
      setIsVisible(true);
      setHasLoaded(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Once visible, stop observing
          observer.disconnect();
        }
      },
      {
        rootMargin, // Load slightly before visible
        threshold: 0,
      }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [rootMargin]);

  // Mark as loaded after first render with content
  useEffect(() => {
    if (isVisible) {
      setHasLoaded(true);
    }
  }, [isVisible]);

  if (!isVisible) {
    return (
      <div 
        ref={ref} 
        style={{ 
          minHeight,
          // Hint to browser about expected content size for better layout stability
          contentVisibility: 'auto',
          containIntrinsicSize: `auto ${minHeight}`,
        }}
      >
        {showSkeleton && (
          <div className="container-wide py-16">
            <div className="flex flex-col items-center gap-4">
              <Skeleton className="h-10 w-64" />
              <Skeleton className="h-6 w-96" />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full mt-8">
                <Skeleton className="h-48 rounded-xl" />
                <Skeleton className="h-48 rounded-xl" />
                <Skeleton className="h-48 rounded-xl" />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return <>{children}</>;
};

export default LazySection;
